import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, Lock, Sparkles, ExternalLink, Loader2, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { authFetch } from "../lib/auth";
import { useSubscription } from "../lib/subscription";
import { toast } from "sonner";
import { API } from "../App";

const Paywall = () => {
  const navigate = useNavigate();
  const { sub, loading } = useSubscription();
  const [redirecting, setRedirecting] = useState(false);

  const handleStripeCheckout = async () => {
    setRedirecting(true);
    try {
      const res = await authFetch(`${API}/subscription/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to start checkout");
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error(e.message || "Could not start checkout");
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  const trialExpired = sub?.subscription_status === "trial_expired";
  const memorySchoolUrl = sub?.memory_school_url || "https://www.skool.com/memory-school-4598/plans";
  const price = sub?.monthly_price_gbp ?? 4.99;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="paywall-page">
      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {trialExpired ? "Your free trial has ended" : "Choose your plan"}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Pick the plan that works for you and keep training your memory.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* MindForge Monthly */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card border-primary/40 h-full relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold tracking-wider">
                MINDFORGE
              </div>
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-primary/15 flex items-center justify-center mb-6">
                  <Brain className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-2">MindForge Monthly</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold">£{price}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-muted-foreground mb-6">
                  Keep using MindForge with all training modes, leaderboards, and history.
                </p>
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Unlimited number, word, and names training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Weekly streak tracking & leaderboards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Cancel anytime</span>
                  </li>
                </ul>
                <Button
                  onClick={handleStripeCheckout}
                  disabled={redirecting}
                  data-testid="stripe-checkout-btn"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 py-6 text-base font-semibold"
                >
                  {redirecting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Redirecting…
                    </>
                  ) : (
                    <>Continue with MindForge</>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Memory School */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="bg-card border-border h-full">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-secondary/15 flex items-center justify-center mb-6">
                  <Sparkles className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="font-heading text-2xl font-semibold mb-2">Memory School</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-4xl font-bold">$37</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <p className="text-muted-foreground mb-6">
                  Get MindForge + every Memory School app, courses, and the private community.
                </p>
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                    <span>Everything in MindForge Monthly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                    <span>Access to all Memory School apps</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                    <span>Live coaching, courses & community</span>
                  </li>
                </ul>
                <Button
                  variant="outline"
                  onClick={() => window.open(memorySchoolUrl, "_blank")}
                  data-testid="memory-school-btn"
                  className="w-full py-6 text-base font-semibold"
                >
                  Join Memory School
                  <ExternalLink className="w-4 h-4 ml-2 opacity-60" />
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Already a member? Just{" "}
                  <button
                    onClick={() => navigate("/")}
                    className="underline hover:text-primary"
                  >
                    log in with that email
                  </button>
                  .
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default Paywall;
